import {
  FormControlLabel,
  Grid2,
  InputLabel,
  Stack,
  Switch,
} from '@mui/material'
import SuggestionAutocompleteField from 'components/fields/suggestion-autocomplete-field/suggestion-autocomplete-field.component'
import TextInputField from 'components/fields/text-input-field/text-input-field.component'
import { CITIZENSHIP_COUNTRIES } from 'pages/driver/data/citizenship-countries'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { AceSection, AceSelectField } from './ace-trip-fields'
import {
  AceTripFormValues,
  CA_PORT_OPTIONS,
  GENDER_OPTIONS,
  LICENSE_PLATE_STATES,
  TRAILER_TYPE_OPTIONS,
  TRUCK_TYPE_OPTIONS,
  US_PORT_OPTIONS,
} from './ace-trip-form'

const AceTripDetailsTab = ({
  lockTripNumber = false,
}: {
  lockTripNumber?: boolean
}) => {
  const { control } = useFormContext<AceTripFormValues>()
  const manifestKind = useWatch({ control, name: 'manifestKind' })
  const portLabel =
    manifestKind === 'ACI'
      ? 'Canadian Port of Arrival *'
      : 'US Port of Arrival *'

  return (
    <Stack spacing={2.5}>
      <AceSection title="Trip Information">
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextInputField
              name="tripNumber"
              label="Trip Number *"
              disabled={lockTripNumber}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <SuggestionAutocompleteField
              name="usPortOfArrival"
              label={portLabel}
              options={
                manifestKind === 'ACI' ? CA_PORT_OPTIONS : US_PORT_OPTIONS
              }
              maxLength={4}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextInputField
              name="estimatedArrival"
              label="Estimated Arrival Date & Time *"
              type="datetime-local"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Stack spacing={1}>
              <InputLabel sx={{ color: 'secondary.dark', fontWeight: 300 }}>
                Auto Send
              </InputLabel>
              <Controller
                name="autoSend"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ ml: 0, height: 40 }}
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                      />
                    }
                    label={field.value ? 'Yes' : 'No'}
                  />
                )}
              />
            </Stack>
          </Grid2>
        </Grid2>
      </AceSection>

      <AceSection title="Truck">
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextInputField name="truckNumber" label="Truck Number *" />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <SuggestionAutocompleteField
              name="truckType"
              label="Truck Type"
              options={TRUCK_TYPE_OPTIONS}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextInputField name="vinNumber" label="VIN Number" />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextInputField
              name="truckLicensePlateNumber"
              label="License Plate Number *"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <SuggestionAutocompleteField
              name="truckLicensePlateState"
              label="License Plate State / Province *"
              options={LICENSE_PLATE_STATES}
            />
          </Grid2>
        </Grid2>
      </AceSection>

      <AceSection title="Trailer">
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <TextInputField
              name="trailer.trailerNumber"
              label="Trailer Number *"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <SuggestionAutocompleteField
              name="trailer.trailerType"
              label="Trailer Type *"
              options={TRAILER_TYPE_OPTIONS}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <TextInputField
              name="trailer.licensePlateNumber"
              label="License Plate Number *"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <SuggestionAutocompleteField
              name="trailer.licensePlateState"
              label="License Plate State / Province *"
              options={LICENSE_PLATE_STATES}
            />
          </Grid2>
        </Grid2>
      </AceSection>

      <AceSection title="Driver">
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextInputField name="driverFirstName" label="First Name *" />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextInputField name="driverLastName" label="Last Name *" />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <AceSelectField
              name="gender"
              label="Gender"
              options={GENDER_OPTIONS}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextInputField
              name="dateOfBirth"
              label="Date of Birth"
              type="date"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <SuggestionAutocompleteField
              name="citizenshipCountry"
              label="Citizenship Country"
              options={CITIZENSHIP_COUNTRIES}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextInputField name="fastCardNumber" label="FAST Card Number" />
          </Grid2>
        </Grid2>
      </AceSection>
    </Stack>
  )
}

export default AceTripDetailsTab
